// components/NotificationBell.js
import React, { useState } from "react";
import {
  IconButton,
  Badge,
  MenuItem,
  Box,
  Typography,
  Avatar,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import BellDropdownMenu from "../common/BellDropdownMenu";
import {
  notificationTriggerLabel,
  notificationAccentColors,
} from "../notificationShared";

function NotificationBell({
  notifications,
  onMarkAsRead,
  iconColor,
  navTone = "dark",
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const isLightNav = navTone === "light";
  const resolvedIconColor = iconColor ?? (isLightNav ? "#334155" : "#fff");
  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleNotificationClick = (notification) => {
    if (!notification.isRead) {
      onMarkAsRead(notification._id);
    }
    handleClose();
  };

  return (
    <>
      <IconButton
        onClick={handleOpen}
        sx={{
          color: resolvedIconColor,
          position: "relative",
          ...(isLightNav && {
            border: "1px solid rgba(15, 23, 42, 0.08)",
            borderRadius: "10px",
            "&:hover": { backgroundColor: "rgba(15, 23, 42, 0.04)" },
          }),
        }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <BellDropdownMenu
        anchorEl={anchorEl}
        onClose={handleClose}
        titleLabel="Notifications"
        unreadCount={unreadCount}
        showFooter={notifications.length > 0}
        footerTo="/notifications"
        footerLabel="View All Notifications"
      >
        {notifications.length === 0 ? (
          <Box sx={{ px: 2, py: 2, display: "flex", justifyContent: "center" }}>
            <Typography variant="body2" sx={{ color: "#666" }}>
              No new notifications
            </Typography>
          </Box>
        ) : (
          notifications.map((n) => {
            const isUnread = !n.isRead;
            const { loginColor, projectColor } =
              notificationAccentColors(isUnread);
            const author = notificationTriggerLabel(n.triggeredBy);
            return (
              <MenuItem
                key={n._id}
                onClick={() => handleNotificationClick(n)}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  py: 1.5,
                  px: 2,
                  borderBottom: "1px solid rgba(0,0,0,0.04)",
                  backgroundColor: isUnread
                    ? "rgba(255, 140, 0, 0.04)"
                    : "transparent",
                  "&:hover": {
                    backgroundColor: isUnread
                      ? "rgba(255, 140, 0, 0.08)"
                      : "rgba(0,0,0,0.04)",
                  },
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", width: "100%" }}
                >
                  <Avatar
                    src={n.triggeredBy?.avatarUrl || "/images/user.png"}
                    sx={{ width: 40, height: 40, mr: 1 }}
                  >
                    {n.triggeredBy?.firstName
                      ? n.triggeredBy.firstName.charAt(0)
                      : ""}
                  </Avatar>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: loginColor, fontWeight: 600 }}
                    >
                      {author}
                    </Typography>
                    {n.projectName && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: projectColor,
                          fontWeight: 600,
                          fontSize: "0.8rem",
                          mt: 0.2,
                        }}
                      >
                        {n.projectName}
                      </Typography>
                    )}
                  </Box>
                  {isUnread && (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: "#FF8C00",
                        ml: 1,
                      }}
                    />
                  )}
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    color: "#4A4A4A",
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    overflowWrap: "break-word",
                    width: "100%",
                    mt: 0.5,
                    fontSize: "0.85rem",
                    lineHeight: 1.4,
                    maxWidth: "280px",
                  }}
                >
                  {n.changes}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    color: "#8F8F8F",
                    mt: 0.5,
                    alignSelf: "flex-end",
                  }}
                >
                  {new Date(n.createdAt).toLocaleString()}
                </Typography>
              </MenuItem>
            );
          })
        )}
      </BellDropdownMenu>
    </>
  );
}

export default NotificationBell;
